import { Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import colors from '../../../../assets/theme/base/colors';
import { IEFConfiguration, IEFTargets, IEFYearTarget } from '../../../../Component/Model/Dto/EmpowermentFinancing';
import { formatPercentage, formatRand, formatWeightingPoints } from '../../../../Helpers/EmpowermentFinancing/formatHelper';

interface ITargetRow {
	id: string;
	label: string;
	ratio?: string;
	points?: string;
	isBold?: boolean;
	fiveYear: number;
	yearValue: (year: IEFYearTarget) => number;
}

interface IProps {
	configuration: IEFConfiguration;
	targets: IEFTargets;
	ratios: Record<string, number>;
	applicableYear: number;
}

const columnCount = 9;

const headerCellSx = {
	bgcolor: colors.empowermentFinancing.tableHeaderBackground,
	color: colors.white.main,
	fontWeight: 700,
	fontSize: '0.688rem',
	textTransform: 'uppercase',
	letterSpacing: '0.06em',
	borderBottom: 'none',
	whiteSpace: 'nowrap' as const,
	py: 1.5,
	px: 1.5,
};

const sectionHeaderCellSx = {
	bgcolor: colors.empowermentFinancing.sectionHeaderBackground,
	fontSize: '0.75rem',
	fontWeight: 700,
	color: 'text.primary',
	textTransform: 'uppercase',
	letterSpacing: '0.06em',
	borderBottomColor: 'grey.200',
	py: 1,
	px: 2,
};

export const TargetsTable = ({ configuration, targets, ratios, applicableYear }: IProps) => {
	const elementName = (key: string) => configuration.elements.find(e => e.key === key)?.name ?? '';
	const elementPoints = (key: string) => configuration.elements.find(e => e.key === key)?.weightingPoints ?? 0;
	const totalPoints = configuration.elements.reduce((sum, element) => sum + element.weightingPoints, 0);

	const topLevelRows: ITargetRow[] = [
		{
			id: 'nonFungible',
			label: elementName('nonFungible'),
			points: formatWeightingPoints(elementPoints('nonFungible')),
			fiveYear: targets.nonFungibleFiveYear,
			yearValue: year => year.nonFungible,
		},
		{
			id: 'targetedInvestments',
			label: elementName('targetedInvestments'),
			points: formatWeightingPoints(elementPoints('targetedInvestments')),
			fiveYear: targets.targetedInvestmentsFiveYear,
			yearValue: year => year.targetedInvestments,
		},
		{
			id: 'transactionFinancingAndBbgf',
			label: elementName('transactionFinancingAndBbgf'),
			points: formatWeightingPoints(elementPoints('transactionFinancingAndBbgf')),
			fiveYear: targets.transactionFinancingAndBbgfFiveYear,
			yearValue: year => year.transactionFinancingAndBbgf,
		},
		{
			id: 'total',
			label: 'Total',
			points: formatWeightingPoints(totalPoints),
			isBold: true,
			fiveYear: targets.totalFiveYear,
			yearValue: year => year.total,
		},
	];

	const subCategoryPoints: Record<string, number> = {
		transformationalInfrastructure: targets.weightingPointsTransformationalInfrastructure,
		blackAgriculturalFinancing: targets.weightingPointsBlackAgriculturalFinancing,
		affordableHousing: targets.weightingPointsAffordableHousing,
		blackBusinessGrowthFunding: targets.weightingPointsBlackBusinessGrowthFunding,
	};

	const subCategoryFiveYear: Record<string, number> = {
		transformationalInfrastructure: targets.transformationalInfrastructureFiveYear,
		blackAgriculturalFinancing: targets.blackAgriculturalFinancingFiveYear,
		affordableHousing: targets.affordableHousingFiveYear,
		blackBusinessGrowthFunding: targets.blackBusinessGrowthFundingFiveYear,
	};

	const subCategoryYearValue: Record<string, (year: IEFYearTarget) => number> = {
		transformationalInfrastructure: year => year.transformationalInfrastructure,
		blackAgriculturalFinancing: year => year.blackAgriculturalFinancing,
		affordableHousing: year => year.affordableHousing,
		blackBusinessGrowthFunding: year => year.blackBusinessGrowthFunding,
	};

	const subCategoryRows: ITargetRow[] = configuration.subCategories.map(subCategory => ({
		id: subCategory.key,
		label: subCategory.name,
		ratio: formatPercentage(ratios[subCategory.key] ?? 0),
		points: formatWeightingPoints(subCategoryPoints[subCategory.key] ?? 0),
		fiveYear: subCategoryFiveYear[subCategory.key] ?? 0,
		yearValue: subCategoryYearValue[subCategory.key],
	}));

	subCategoryRows.push({
		id: 'subTotal',
		label: 'Total',
		isBold: true,
		ratio: formatPercentage(100),
		points: formatWeightingPoints(configuration.targetedInvestmentsWeightingPoints),
		fiveYear: targets.targetedInvestmentsFiveYear,
		yearValue: year => year.targetedInvestments,
	});

	const renderRow = (row: ITargetRow, index: number, isLast: boolean) => {
		let rowBackground = colors.white.main;
		if (isLast) {
			rowBackground = colors.empowermentFinancing.totalRowBackground;
		} else if (index % 2 !== 0) {
			rowBackground = colors.empowermentFinancing.alternateRowBackground;
		}

		return (
			<TableRow key={row.id} sx={{ bgcolor: rowBackground }}>
				<TableCell
					sx={{
						fontSize: '0.8125rem',
						fontWeight: row.isBold ? 600 : 400,
						color: 'text.primary',
						borderBottomColor: 'grey.200',
						py: 1,
						px: 2,
					}}
				>
					{row.label}
				</TableCell>
				<TableCell align="center" sx={{ fontSize: '0.8125rem', color: 'text.primary', borderBottomColor: 'grey.200', py: 1, px: 1.5 }}>
					{row.ratio ?? ''}
				</TableCell>
				<TableCell align="center" sx={{ fontSize: '0.8125rem', color: 'text.primary', borderBottomColor: 'grey.200', py: 1, px: 1.5 }}>
					{row.points ?? ''}
				</TableCell>
				<TableCell align="right" sx={{ fontSize: '0.8125rem', color: 'text.primary', borderBottomColor: 'grey.200', py: 1, px: 1.5 }}>
					{formatRand(row.fiveYear)}
				</TableCell>
				{targets.years.map(year => {
					const isApplicableYear = year.year === applicableYear;
					return (
						<TableCell
							key={year.year}
							align="right"
							sx={{
								fontSize: '0.8125rem',
								fontWeight: isApplicableYear ? 600 : 400,
								color: isApplicableYear ? 'primary.main' : 'text.primary',
								bgcolor: isApplicableYear ? colors.empowermentFinancing.applicableYearCellBg : 'inherit',
								borderBottomColor: 'grey.200',
								py: 1,
								px: 1.5,
							}}
						>
							{formatRand(row.yearValue(year))}
						</TableCell>
					);
				})}
			</TableRow>
		);
	};

	return (
		<Card sx={{ mb: 2, overflow: 'hidden' }}>
			<TableContainer>
				<Table size="small" sx={{ tableLayout: 'fixed' }}>
					<colgroup>
						<col />
						<col style={{ width: 90 }} />
						<col style={{ width: 115 }} />
						<col style={{ width: 155 }} />
						{targets.years.map(year => (
							<col key={year.year} style={{ width: 135 }} />
						))}
					</colgroup>
					<TableHead sx={{ display: 'table-header-group' }}>
						<TableRow>
							<TableCell sx={headerCellSx}>Measurement Criteria</TableCell>
							<TableCell align="center" sx={headerCellSx}>
								Ratio
							</TableCell>
							<TableCell align="center" sx={headerCellSx}>
								Weighting Pts
							</TableCell>
							<TableCell align="right" sx={headerCellSx}>
								5-Year Total
							</TableCell>
							{targets.years.map(year => (
								<TableCell
									key={year.year}
									align="right"
									sx={{
										...headerCellSx,
										bgcolor:
											year.year === applicableYear
												? colors.empowermentFinancing.applicableYearHeaderBg
												: colors.empowermentFinancing.tableHeaderBackground,
									}}
								>
									{year.year}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell colSpan={columnCount} sx={sectionHeaderCellSx}>
								Table 1 - Top-Level Targets by Element
							</TableCell>
						</TableRow>
						{topLevelRows.map((row, index) => renderRow(row, index, index === topLevelRows.length - 1))}

						<TableRow>
							<TableCell
								colSpan={columnCount}
								sx={{ ...sectionHeaderCellSx, borderTop: '2px solid', borderTopColor: 'grey.400' }}
							>
								Table 2 - Targeted Investments Sub-Category Breakdown
							</TableCell>
						</TableRow>
						{subCategoryRows.map((row, index) => renderRow(row, index, index === subCategoryRows.length - 1))}
					</TableBody>
				</Table>
			</TableContainer>
		</Card>
	);
};
